import { AnimatePresence, motion } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { ReactNode, useEffect } from "react";

interface DrawerProps {
  show: boolean;
  onClose: () => void;
  title?: string;
  children?: ReactNode;
  closeButton?: boolean;
  direction?: "top" | "bottom" | "left" | "right";
  size?: "sm" | "md" | "lg" | "full" | "half" | "1/3";
  className?: string;
  footer?: ReactNode;
  isMobile?: boolean;
}

const Drawer = ({
  show,
  onClose,
  title,
  children,
  closeButton = true,
  direction = "bottom",
  size = "md",
  className = "",
  footer,
  isMobile = false,
}: DrawerProps) => {
  const getDrawerSize = () => {
    if (direction === "left" || direction === "right") {
      switch (size) {
        case "sm":
          return "h-full w-64";
        case "lg":
          return "h-full w-96";
        case "full":
          return "h-full w-full";
        case "half":
          return "h-full w-1/2";
        default:
          return "h-full w-80";
      }
    } else {
      switch (size) {
        case "sm":
          return "w-64 mx-auto";
        case "lg":
          return "w-96 mx-auto";
        case "full":
          return "w-full";
        case "half":
          return "w-1/2 mx-auto";
        case "1/3":
          return "w-1/3 mx-auto";
        default:
          return "w-80 mx-auto";
      }
    }
  };

  const getDrawerDirection = () => {
    switch (direction) {
      case "top":
        return "top-0 left-0 right-0";
      case "bottom":
        return "bottom-0 left-0 right-0";
      case "left":
        return "top-0 left-0 bottom-0";
      case "right":
        return "top-0 right-0 bottom-0";
      default:
        return "";
    }
  };

  const getDrawerAnimation = () => {
    switch (direction) {
      case "top":
        return {
          initial: { y: "-100%" },
          animate: { y: 0 },
          exit: { y: "-100%" },
        };
      case "bottom":
        return {
          initial: { y: "100%" },
          animate: { y: 0 },
          exit: { y: "100%" },
        };
      case "left":
        return {
          initial: { x: "-100%" },
          animate: { x: 0 },
          exit: { x: "-100%" },
        };
      case "right":
        return {
          initial: { x: "100%" },
          animate: { x: 0 },
          exit: { x: "100%" },
        };
      default:
        return {
          initial: { x: "100%" },
          animate: { x: 0 },
          exit: { x: "100%" },
        };
    }
  };

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            {...getDrawerAnimation()}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className={`fixed z-50 bg-white dark:bg-neutral-900 shadow-xl ${getDrawerDirection()} ${getDrawerSize()} ${className}
              ${direction === "top" ? "rounded-b-xl" : ""}
              ${direction === "bottom" ? "rounded-t-xl" : ""}
              ${direction === "left" ? "rounded-r-xl" : ""}
              ${direction === "right" ? "rounded-l-xl" : ""}
            `}
          >
            {/* Header */}
            {(title || closeButton) && (
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-neutral-700">
                {title && (
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                  </h3>
                )}
                {closeButton && (
                  <button
                    onClick={onClose}
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-neutral-600 rounded-full p-1 transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div
              className={`p-4 ${
                isMobile ? "overflow-y-auto max-h-[calc(100vh-70px)]" : ""
              } ${className.includes("flex") ? "flex-1 flex flex-col overflow-hidden" : ""}`}
            >
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div
                className={`flex items-center gap-2 p-4 border-t border-gray-100 dark:border-neutral-700`}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Drawer;
